import { useCallback, useEffect, useState } from 'react';
import { MdAcUnit, MdEdit, MdSearch } from 'react-icons/md';
import { useLocation, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/owner/PageHeader';
import GymIdMemberSearch from '../../components/receptionist/GymIdMemberSearch';
import {
  ColumnFilterHeader,
  formatDateFilterValue,
  useColumnFilters,
} from '../../components/admin/ColumnFilterHeader';
import { usePanelToast } from '../../context/PanelToastContext';
import {
  fetchFreezes,
  fetchFreezesPage,
  fetchMembersPage,
  createFreeze,
  updateFreeze,
} from '../../services/receptionist/receptionistApi';
import { RECEPTIONIST_BRANCH_EVENT } from '../../services/gym/branchContext';
import { useServerPagedList } from '../../hooks/useServerPagedList';
import TablePagination from '../../components/shared/TablePagination';
import {
  exportFreezeAccounts,
  exportFreezeCsv,
  exportFreezeExcel,
  exportFreezePdf,
  printFreezeList,
} from './exportFreezeAccounts';
import { dedupeFreezes, dedupeMembers } from '../../utils/dedupeList';
import '../owner/ownerPages.css';
import '../owner/subscriptions/subscriptions.css';
import '../admin/memberships/memberships.css';
import '../admin/packages/packages.css';
import './members/members.css';

function autoExtendLabel(account) {
  return account.autoExtend ? 'Yes' : 'No';
}

function accountStatusLabel(account) {
  return account.accountStatus === 'inactive' ? 'Inactive' : 'Active';
}

function getTodayDateStr() {
  return new Date().toISOString().slice(0, 10);
}

function getEmptyFreezeForm() {
  return {
    memberId: '',
    member: '',
    gymId: '',
    startDate: getTodayDateStr(),
    endDate: '',
    reason: '',
    autoExtend: false,
  };
}

function resolveGymId(record) {
  return record.gymId || '—';
}

function FreezeAccounts() {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = usePanelToast();
  const [pickerMembers, setPickerMembers] = useState([]);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [freezeForm, setFreezeForm] = useState(getEmptyFreezeForm);
  const [gymIdQuery, setGymIdQuery] = useState('');
  const [editingId, setEditingId] = useState('');
  const [search, setSearch] = useState('');
  const [memberLocked, setMemberLocked] = useState(false);
  const {
    filters,
    updateFilter,
    openFilterKey,
    toggleFilter,
  } = useColumnFilters({
    gymId: '',
    member: '',
    startDate: '',
    endDate: '',
    reason: '',
    autoExtend: '',
    status: '',
  });

  const fetchPage = useCallback((params) => fetchFreezesPage(params), []);
  const {
    rows: list,
    total,
    page,
    setPage,
    totalPages,
    from,
    to,
    loading,
    error,
    setError,
    reload,
    nextPage,
    prevPage,
  } = useServerPagedList({ fetcher: fetchPage, search, filters });

  useEffect(() => {
    const onBranch = () => reload();
    window.addEventListener(RECEPTIONIST_BRANCH_EVENT, onBranch);
    const retry = window.setTimeout(() => reload(), 800);
    return () => {
      window.removeEventListener(RECEPTIONIST_BRANCH_EVENT, onBranch);
      window.clearTimeout(retry);
    };
  }, [reload]);

  // Server-search members for the freeze modal picker
  useEffect(() => {
    if (!showModal || memberLocked) return undefined;
    const q = gymIdQuery.trim();
    if (!q || freezeForm.member) return undefined;
    const timer = setTimeout(async () => {
      try {
        const result = await fetchMembersPage({ search: q, pageSize: 20 });
        setPickerMembers(dedupeMembers(result.rows || []));
      } catch {
        setPickerMembers([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [gymIdQuery, showModal, memberLocked, freezeForm.member]);

  const openFreezeForMember = useCallback((member) => {
    if (!member) return;
    const gymId = member.gymId || '';
    setFormKey((k) => k + 1);
    setEditingId('');
    setFormError('');
    setFreezeForm({
      memberId: member.id || '',
      member: member.name || '',
      gymId,
      startDate: getTodayDateStr(),
      endDate: '',
      reason: '',
      autoExtend: false,
    });
    setGymIdQuery(gymId);
    setPickerMembers(member.id ? [member] : []);
    setMemberLocked(true);
    setShowModal(true);
  }, []);

  useEffect(() => {
    const incoming = location.state?.freezeMember;
    if (!incoming) return;
    openFreezeForMember(incoming);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate, openFreezeForMember]);

  const filteredList = list;

  const handleExport = async (exporterFn) => {
    try {
      const all = await fetchFreezes();
      exporterFn(dedupeFreezes(all));
    } catch (err) {
      setError(err.message || 'Export failed');
    }
  };

  const openModal = () => {
    setFormKey((k) => k + 1);
    setEditingId('');
    setFormError('');
    setFreezeForm(getEmptyFreezeForm());
    setGymIdQuery('');
    setPickerMembers([]);
    setMemberLocked(false);
    setShowModal(true);
  };

  const openEditModal = (account) => {
    setFormKey((k) => k + 1);
    setEditingId(account.id);
    setFormError('');
    const gymId = resolveGymId(account);
    setFreezeForm({
      memberId: account.memberId || '',
      member: account.member,
      gymId: gymId === '—' ? '' : gymId,
      startDate: account.startDate,
      endDate: account.endDate,
      reason: account.reason || '',
      autoExtend: Boolean(account.autoExtend),
    });
    setGymIdQuery(gymId === '—' ? '' : gymId);
    setMemberLocked(true);
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setEditingId('');
    setMemberLocked(false);
    setFormError('');
    setPickerMembers([]);
  };

  const handleFreezeSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!freezeForm.member || (!freezeForm.memberId && !freezeForm.gymId)) {
      setFormError('Search and select a member by Serial No before freezing.');
      return;
    }
    if (!freezeForm.startDate || !freezeForm.endDate) {
      setFormError('Start and end dates are required.');
      return;
    }
    if (freezeForm.endDate < freezeForm.startDate) {
      setFormError('End date must be on or after the start date.');
      return;
    }

    const memberId = freezeForm.memberId
      || pickerMembers.find((m) => m.name === freezeForm.member)?.id
      || pickerMembers.find((m) => String(m.gymId || '').toLowerCase() === String(freezeForm.gymId || '').toLowerCase())?.id;

    if (!memberId) {
      setFormError('Selected member not found. Search again and pick a match.');
      toast.showError('Selected member not found.');
      return;
    }

    try {
      setSaving(true);
      if (editingId) {
        await updateFreeze(editingId, freezeForm, memberId);
        toast.showUpdated('Freeze account');
      } else {
        await createFreeze(freezeForm, memberId);
        toast.showAdded('Freeze account');
      }
      setShowModal(false);
      setEditingId('');
      setMemberLocked(false);
      setFormError('');
      setPickerMembers([]);
      await reload();
    } catch (err) {
      const message = err.message || 'Failed to save freeze account';
      setFormError(message);
      toast.showError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader title="Freeze Accounts" subtitle="Manage membership freeze periods and extensions" variant="banner">
        <button type="button" className="owner-btn owner-btn-primary" onClick={openModal}>
          <MdAcUnit style={{ marginRight: 8 }} />
          Freeze
        </button>
      </PageHeader>

      {error && <p className="owner-placeholder-text owner-mb-16">{error}</p>}
      {loading && <p className="owner-placeholder-text owner-mb-16">Loading freeze accounts...</p>}

      <div className="rec-member-toolbar">
        <div className="rec-member-toolbar-left">
          <div className="rec-member-search-field">
            <MdSearch className="rec-member-search-icon" />
            <input
              type="text"
              className="owner-form-input rec-member-search-input"
              placeholder="Search by serial no..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="rec-member-toolbar-actions">
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => handleExport(printFreezeList)}>
            Print
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => handleExport(exportFreezeCsv)}>
            Export CSV
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => handleExport(exportFreezePdf)}>
            Export PDF
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => handleExport(exportFreezeExcel)}>
            Export Excel
          </button>
          <button type="button" className="owner-btn owner-btn-primary owner-btn-sm" onClick={() => handleExport(exportFreezeAccounts)}>
            Export
          </button>
        </div>
      </div>

      <div className="owner-card rec-panel-card rec-member-list-card">
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Serial No"
                  filterKey="gymId"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search serial..."
                />
                <ColumnFilterHeader
                  label="Member"
                  filterKey="member"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search member..."
                />
                <ColumnFilterHeader
                  label="Freeze Start"
                  filterKey="startDate"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="DD-MM-YYYY"
                  formatValue={formatDateFilterValue}
                />
                <ColumnFilterHeader
                  label="Freeze End"
                  filterKey="endDate"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="DD-MM-YYYY"
                  formatValue={formatDateFilterValue}
                />
                <ColumnFilterHeader
                  label="Reason"
                  filterKey="reason"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search reason..."
                />
                <ColumnFilterHeader
                  label="Auto Extend"
                  filterKey="autoExtend"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={['Yes', 'No']}
                  allLabel="All Auto Extend"
                />
                <ColumnFilterHeader
                  label="Status"
                  filterKey="status"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={['Active', 'Inactive']}
                  allLabel="All Status"
                />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredList.length > 0 ? (
                filteredList.map((a) => (
                  <tr key={a.id}>
                    <td className="rec-member-gym-id">{resolveGymId(a)}</td>
                    <td>{a.member}</td>
                    <td>{a.startDate}</td>
                    <td>{a.endDate}</td>
                    <td>{a.reason || '—'}</td>
                    <td>
                      <span className={`owner-badge ${a.autoExtend ? 'owner-badge-success' : 'owner-badge-warning'}`}>
                        {a.autoExtend ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td>
                      <span className={`owner-badge ${a.accountStatus === 'inactive' ? 'owner-badge-warning' : 'owner-badge-success'}`}>
                        {a.accountStatus === 'inactive' ? 'Inactive' : 'Active'}
                      </span>
                    </td>
                    <td>
                      <div className="rec-member-actions-cell">
                        <button
                          type="button"
                          className="rec-member-icon-btn rec-member-icon-btn-edit"
                          onClick={() => openEditModal(a)}
                          aria-label={`Edit ${a.member}`}
                          title="Edit"
                        >
                          <MdEdit />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="owner-placeholder-text">
                    {search.trim() ? 'No freeze accounts match this serial no.' : 'No freeze accounts found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <TablePagination
          page={page}
          totalPages={totalPages}
          total={total}
          from={from}
          to={to}
          onPageChange={setPage}
          onPrev={prevPage}
          onNext={nextPage}
        />
      </div>

      {showModal && (
        <div className="subscription-modal-overlay" onClick={closeModal}>
          <div
            className="subscription-modal admin-form-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="freeze-account-title"
          >
            <div className="subscription-modal-header">
              <h2 id="freeze-account-title" className="subscription-modal-title">{editingId ? 'Edit Freeze Account' : 'Freeze Membership'}</h2>
              <button type="button" className="subscription-modal-close" onClick={closeModal} aria-label="Close" disabled={saving}>×</button>
            </div>
            <form key={formKey} className="subscription-form" onSubmit={handleFreezeSubmit}>
              <div className="membership-form-grid">
                {memberLocked ? (
                  <>
                    <div className="owner-form-group membership-form-full">
                      <label className="owner-form-label" htmlFor="freeze-gym-id-locked">Serial No</label>
                      <input
                        id="freeze-gym-id-locked"
                        type="text"
                        className="owner-form-input rec-member-country-readonly"
                        value={freezeForm.gymId || '—'}
                        readOnly
                        aria-readonly="true"
                      />
                    </div>
                    <div className="owner-form-group membership-form-full">
                      <label className="owner-form-label" htmlFor="freeze-member-locked">Member</label>
                      <input
                        id="freeze-member-locked"
                        type="text"
                        className="owner-form-input rec-member-country-readonly"
                        value={freezeForm.member || '—'}
                        readOnly
                        aria-readonly="true"
                      />
                    </div>
                  </>
                ) : (
                  <GymIdMemberSearch
                    id="freeze-gym-id"
                    label="Serial No"
                    className="owner-form-group membership-form-full"
                    query={gymIdQuery}
                    onQueryChange={setGymIdQuery}
                    selectedName={freezeForm.member}
                    selectedGymId={freezeForm.gymId}
                    onSelect={({ name, gymId, member }) => {
                      setFormError('');
                      setFreezeForm((p) => ({
                        ...p,
                        member: name,
                        gymId,
                        memberId: member?.id || '',
                      }));
                      if (member) setPickerMembers([member]);
                      if (gymId) setGymIdQuery(gymId);
                    }}
                    members={pickerMembers}
                  />
                )}
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="freeze-start">Freeze Start Date</label>
                  <input
                    id="freeze-start"
                    type="date"
                    className="owner-form-input"
                    value={freezeForm.startDate}
                    onChange={(e) => setFreezeForm((p) => ({ ...p, startDate: e.target.value }))}
                    required
                    disabled={saving}
                  />
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="freeze-end">Freeze End Date</label>
                  <input
                    id="freeze-end"
                    type="date"
                    className="owner-form-input"
                    value={freezeForm.endDate}
                    min={freezeForm.startDate}
                    onChange={(e) => setFreezeForm((p) => ({ ...p, endDate: e.target.value }))}
                    required
                    disabled={saving}
                  />
                </div>
                <div className="owner-form-group membership-form-full">
                  <label className="owner-form-label" htmlFor="freeze-reason">Reason Details</label>
                  <textarea
                    id="freeze-reason"
                    className="owner-form-input owner-form-textarea"
                    rows={3}
                    value={freezeForm.reason}
                    onChange={(e) => setFreezeForm((p) => ({ ...p, reason: e.target.value }))}
                    placeholder="Enter reason for freezing membership"
                    disabled={saving}
                  />
                </div>
                <div className="owner-form-group membership-form-full">
                  <label className="package-toggle" htmlFor="freeze-auto-extend">
                    <input
                      id="freeze-auto-extend"
                      type="checkbox"
                      checked={freezeForm.autoExtend}
                      onChange={(e) => setFreezeForm((p) => ({ ...p, autoExtend: e.target.checked }))}
                      disabled={saving}
                    />
                    <span>Auto extend membership after freeze period</span>
                  </label>
                </div>
              </div>
              {formError && (
                <p className="membership-modal-message" style={{ color: 'var(--color-danger)' }}>
                  {formError}
                </p>
              )}
              <div className="subscription-form-actions">
                <button type="submit" className="owner-btn owner-btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Freeze Membership'}
                </button>
                <button type="button" className="owner-btn owner-btn-secondary" onClick={closeModal} disabled={saving}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default FreezeAccounts;
